import { useState, useEffect } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import {
  Drawer,
  List,
  Typography,
  Divider,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Button,
  IconButton,
  useMediaQuery,
} from '@mui/material';

import MenuIcon from '@mui/icons-material/Menu';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import LanguageIcon from '@mui/icons-material/Language';
import SettingsIcon from '@mui/icons-material/Settings';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import RouterIcon from '@mui/icons-material/Router';
import BusinessIcon from '@mui/icons-material/Business';
import RedeemIcon from '@mui/icons-material/Redeem';
import LockIcon from '@mui/icons-material/Lock';
import SmartphoneIcon from '@mui/icons-material/Smartphone';
import AppSettingsAltIcon from '@mui/icons-material/AppSettingsAlt';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';

import { useSidebar } from '../../helpers/contextHelpers';
import { useTranslation } from '../../helpers/i18n/useTranslation';
import { checkRoles } from '@videostream/role-checker';

const drawerWidth = 192;
const collapsedDrawerWidth = 80;

export const SIDEBAR_WIDTH = drawerWidth + 20;
export const COLLAPSED_SIDEBAR_WIDTH = collapsedDrawerWidth + 20
export const DRAWER_WIDTH = drawerWidth;
export const SIDEBAR_MARGIN = 45;

export default function Sidebar({
  pageName,
  currentUser,
  currentUserIsAdmin,
}) {
  const router = useRouter();
  const { expanded, setExpanded } = useSidebar();
  const [hasClicked, setHasClicked] = useState(false);

  const sm = useMediaQuery('(max-width:732px)');

  useEffect(() => {
    if (!hasClicked && sm) {
      setExpanded(false);
    } else if (!hasClicked) {
      setExpanded(true);
    }
  }, [sm, setExpanded, hasClicked]);

  const handleSidebarChange = () => {
    setExpanded(!expanded);

    if (!hasClicked) {
      setHasClicked(true);
    }
  }

  // split on capital letters
  const formattedPageName = pageName?.split(/(?=[A-Z])/)?.[0];
  const homeURL = checkRoles(currentUser, [`canListTenants:*`]) ? '/tenants' : `/tenants/${currentUser.tenantId}/Installations`;
  return (
    <nav>
      {/* Hack: so installation table doesn't show behind sidebar when scrolling horizontally */}
      <Box sx={{
        backgroundColor: theme => theme.palette.common.white,
        position: 'fixed',
        width: expanded ? `${SIDEBAR_WIDTH}px` : `${COLLAPSED_SIDEBAR_WIDTH}px`,
        height: '100%',
        zIndex: '9',
      }}></Box>

      {/* So sidebar will take up space in layout */}
      <Box sx={{ minWidth: expanded ? `${SIDEBAR_WIDTH}px` : `${COLLAPSED_SIDEBAR_WIDTH}px` }}></Box>

      <Drawer
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          flexShrink: 0,
          height: 'calc(100vh - 40px)',
          minHeight: '200px',
          maxHeight: '1040px',
          '& .MuiDrawer-paper': {
            width: expanded ? drawerWidth : collapsedDrawerWidth,
            height: 'calc(100vh - 40px)',
            maxHeight: '1040px',
            boxSizing: 'border-box',
            marginLeft: '20px',
            marginTop: '20px',
            borderRadius: expanded ? '16px' : '40px',
            backgroundColor: theme => theme.palette.common.surface1,
          },
          position: 'absolute',
          zIndex: '10',
        }}
        variant="permanent"
        anchor="left"
      >
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          height: 'calc(100% - 35px)',
        }}>
          <Box>
            <Logo expanded={expanded} homeURL={homeURL} />
            {formattedPageName === 'tenants' ?
              <TenantsListSidebarButtons expanded={expanded} />
              :
              <TenantViewSidebarButtons expanded={expanded} router={router} pageName={pageName} currentUser={currentUser} />}
          </Box>
          <SidebarCollapseButton
            expanded={expanded}
            onClick={handleSidebarChange}
          />
        </Box>

      </Drawer>
    </nav>
  )
}

function Logo({ expanded = false, homeURL }) {
  const { translate } = useTranslation();
  if (expanded) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', marginTop: '28px', alignItems: 'center', marginBottom: '32px' }}>
        <Link href={homeURL} passHref>
          <a data-testid="homepage-link"><img src="/logo-dark.svg" alt={translate('go_to_homepage')} style={{ height: '34px', cursor: 'pointer' }} /></a>
        </Link>
        <Divider variant="middle" sx={{ width: '168px', marginTop: '13px', marginBottom: '6px' }} />
        <Typography
          sx={{
            fontWeight: 300,
            fontSize: '18px',
            letterSpacing: '0.25em',
          }}
        >
          CERTIFY
        </Typography>
      </Box>)
  } else {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: '28px', marginBottom: '79px' }}>
        <img src="/icon-logo.svg" style={{ height: '34px' }} />
      </Box>
    )
  }
}

function SidebarButton({ expanded = false, iconSrc, text, url, active = false }) {
  return (
    <ListItem key={text} disablePadding>
      <Link href={url} passHref>
        <ListItemButton
          aria-label={text}
          sx={{
            maxWidth: expanded ? '168px' : '56px',
            maxHeight: '56px',
            minHeight: '56px',
            marginLeft: '12px',
            borderRadius: '60px',
            '&:hover': {
              backgroundColor: theme => theme.palette.common.surface3,
            },
            '&:focus': {
              backgroundColor: '#CAEAE3',
            },
            backgroundColor: active ? '#CAEAE3' : null,
          }}>
          <ListItemIcon sx={{
            minWidth: '24px',
            maxWidth: '24px',
          }}>
            {typeof iconSrc === 'object' ? iconSrc : <img src={iconSrc} alt={expanded ? "" : text} height='24px' width='24px' />}
          </ListItemIcon>
          {expanded ? (
            <ListItemText
              primary={<Typography variant="body2" sx={{ fontWeight: active ? 600 : 400 }}>{text}</Typography>}
              sx={{ marginLeft: '10px', color: theme => theme.palette.secondary.dark }}
              disableTypography
            />
          )
            :
            null
          }
        </ListItemButton>
      </Link>
    </ListItem>
  )
}

function SidebarCollapseButton({ expanded = false, onClick }) {
  const { translate } = useTranslation();
  if (expanded) {
    return (
      <Button
        onClick={onClick}
        startIcon={<MenuOpenIcon sx={{ color: theme => theme.palette.common.black }} />}
        sx={{ textTransform: 'none' }}
      >
        <Typography variant="button" sx={{ color: theme => theme.palette.common.buttonText, textTransform: 'none' }}>
          {translate('collapse')}
        </Typography>
      </Button>
    )
  } else {
    return (
      <IconButton
        onClick={onClick}
        aria-label={translate('expand_sidebar')}
        sx={{
          color: theme => theme.palette.common.black,
          height: '56px',
          width: '56px',
          alignSelf: 'center',
          marginBottom: '-16px',
        }}
      >
        <MenuIcon />
      </IconButton>
    )
  }
}

function TenantsListSidebarButtons({ expanded }) {
  const { translate } = useTranslation();
  return (
    <List>
      <SidebarButton
        expanded={expanded}
        url='/'
        iconSrc={<BusinessIcon />}
        text={translate('tenants')}
        active
      />
    </List>
  )
}

function TenantViewSidebarButtons({ router, expanded, pageName, currentUser }) {
  const { translate } = useTranslation();
  
  return (
    <List>
      <SidebarButton
        expanded={expanded}
        url={`/tenants/${router.query.tenant_name}/Installations`}
        iconSrc="/icon-wifi.svg"
        text={translate('installations')}
        active={pageName === 'Installations'}
      />
      <SidebarButton
        expanded={expanded}
        url={`/tenants/${router.query.tenant_name}/Users`}
        iconSrc={<PeopleAltIcon sx={{ color: 'black', height: '24px', width: '24px' }} />}
        text={translate('users')}
        active={pageName === 'Users'}
      />
    
      <SidebarButton
        expanded={expanded}
        url={`/tenants/${router.query.tenant_name}/Reports`}
        iconSrc={"/reports-icon.svg"}
        text={translate('reports')}
        active={pageName === 'Reports'}
      />

      <SidebarButton
        expanded={expanded}
        url={`/tenants/${router.query.tenant_name}/Insights`}
        iconSrc={<TrendingUpIcon sx={{ color: 'black', height: '24px', width: '24px' }} />}
        text={"Insights"}
        active={pageName === 'Insights'}
      />

      {expanded &&
        <ListItem>
          <Box sx={{ height: '56px', display: 'flex', alignItems: 'center' }}>
            <ListItemText
              primary={<Typography variant="subhead2">{translate('settings')}</Typography>}
              sx={{ marginLeft: '10px', color: theme => theme.palette.secondary.dark }}
              disableTypography
            />
          </Box>
        </ListItem>}
      <SidebarButton
        expanded={expanded}
        url={`/tenants/${router.query.tenant_name}/EquipmentSettings`}
        iconSrc={<RouterIcon sx={{ color: 'black', height: '24px', width: '24px' }} />}
        text={translate('equipment')}
        active={pageName === 'EquipmentSettings'}
      />
      { checkRoles(currentUser, [ 'createTenant:*']) && 
      <>
        <SidebarButton
          expanded={expanded}
          url={`/tenants/${router.query.tenant_name}/MainSettings`}
          iconSrc={<SettingsIcon sx={{ color: 'black', height: '24px', width: '24px' }} />}
          text={translate('main_settings')}
          active={pageName === 'MainSettings'}
        />
        <SidebarButton
          expanded={expanded}
          url={`/tenants/${router.query.tenant_name}/TerminologySettings`}
          iconSrc={<LanguageIcon sx={{ color: 'black', height: '24px', width: '24px' }} />}
          text={translate('terminology')}
          active={pageName === 'TerminologySettings'}
        />
        <SidebarButton
          expanded={expanded}
          url={`/tenants/${router.query.tenant_name}/SecuritySettings`}
          iconSrc={<LockIcon sx={{ color: 'black', height: '24px', width: '24px' }} />}
          text="Security"
          active={pageName === 'SecuritySettings'}
        /> 
          <SidebarButton
          expanded={expanded}
          url={`/tenants/${router.query.tenant_name}/SMSSettings`}
          iconSrc={<SmartphoneIcon sx={{ color: 'black', height: '24px', width: '24px' }} />}
          text={translate('sms_settings')}
          active={pageName === 'SMSSettings'} />

          <SidebarButton
          expanded={expanded}
          url={`/tenants/${router.query.tenant_name}/MobileSettings`}
          iconSrc={<AppSettingsAltIcon sx={{ color: 'black', height: '24px', width: '24px' }} />}
          text={translate('mobile_settings')}
          active={pageName === 'MobileSettings'} />
        
      </>
        }
      <SidebarButton
        expanded={expanded}
        url={`/tenants/${router.query.tenant_name}/CertificateSettings`}
        iconSrc={<RedeemIcon sx={{ color: 'black', height: '24px', width: '24px' }} />}
        text={translate('certificate')}
        active={pageName === 'CertificateSettings'}
      />
    </List>
  )
}